import { BetsAPI, BetType, SportType, TeamLeagueSearch, TeamLeagueType, useAppSelector } from '@supersports/services';
import { format, formatISO } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { bn, enUS, id, km, ms, pt, th, vi, zhCN } from 'date-fns/locale';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { ThemeType } from '../constants/ThemeType';
import TransType from '../constants/TransType';
import { useIsMobile } from '../hooks';

export * from './cn';
export * from './parentPage';

export const supportLangs = ['en', 'vi', 'th', 'zh', 'id', 'ms', 'my', 'bn', 'pt'];
export const specificMerchant = ['xx5', 'aw8'];
const merchantNeedToDisplayLogo = ['055', 'j88', 'xx5'];

export interface RequestIconsResult {
  urlForecast: string;
  urlLiveCenter: string;
  urlLiveStatic: string;
  urlLiveStreaming: string;
  urlPoolingCountdownRefresh: string;
  urlPoolingCounterRefresh: string;
  urlWelcomeBetCredit: string;
  urlWelcomeSumForecast: string;
  urlCarousel: string;
  urlMiniBetList: string;
  urlShowMiniBetList: string;
  urlMainAccordionIcon: string;
  urlNavigationBarIcons: {
    urlBellIcon: string;
    urlBagIcon: string;
    urlSpeakerIcon: string;
    urlSoundOffIcon: string;
  };
}

export interface RequestIconsParams {
  isLiveCenter?: boolean;
  isForecast?: boolean;
  isLiveStatic?: boolean;
  isLiveStreaming?: boolean;
  isReloadHeader?: boolean;
  isPoolingCountdown?: boolean;
  isPoolingCounter?: boolean;
  isShowBetCredit?: boolean;
  isShowSumForecast?: boolean;
  isCarouselArrowNext?: boolean;
  isMiniBetListRefresh?: boolean;
  isShowMiniBetList?: boolean;
  isMainAccordionIcon?: boolean;
  iconsComeFrom?: string;
}

/** Merge classes with tailwind-merge with clsx full feature */
export const Utils = {
  formatDate(timestamp: number | string | Date, formatStr = 'd MMM HH:mm') {
    const date =
      timestamp instanceof Date ? timestamp : new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);

    let _locale = enUS;

    const full = this.convertToLocale(localStorage.getItem('lang') ?? 'en-US');
    const [lang] = full.split('-');

    switch (lang.toLowerCase()) {
      case 'vi':
        _locale = vi;
        break;
      case 'th':
        _locale = th;
        break;
      case 'zh':
        _locale = zhCN;
        break;
      case 'id':
        _locale = id;
        break;
      case 'km':
      case 'kh':
        _locale = km;
        break;
      case 'ms':
      case 'my':
        _locale = ms;
        break;
      case 'bn':
        _locale = bn;
        break;
      case 'pt':
        _locale = pt;
        break;
      default:
        break;
    }

    return format(date, formatStr, {
      locale: _locale,
    });
  },

  formatNumber(value: number | undefined, format = 'en-US', options?: Intl.NumberFormatOptions) {
    if (value === undefined) return '';
    return new Intl.NumberFormat(format, options).format(value);
  },

  convertDatetoUTC(date: Date) {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatISO(zonedTimeToUtc(date, timeZone));
  },

  calcMarket(item: any) {
    let c = 0;

    item.HDP_List?.forEach((match: { IsHide: boolean }) => {
      if (match.IsHide === false) {
        c++;
      }
    });

    item.OU_List?.forEach((match: { IsHide: boolean }) => {
      if (match.IsHide === false) {
        c++;
      }
    });

    for (let betTypeKey in BetType) {
      if (isNaN(Number(betTypeKey))) {
        if (item[betTypeKey] && item[betTypeKey]?.IsHide === false) {
          c++;
        }
      }
    }

    return c;
  },

  prependClass(classes: string, prefix: string) {
    return classes
      .split(' ')
      .map((c) => `${prefix}${c}`)
      .join(' ');
  },

  getLangAndRegion(text: string) {
    try {
      return text.includes('_') ? text.split('_') : text.split('-');
    } catch (error) {
      return ['en', 'US'];
    }
  },

  convertToLocale(text: string) {
    try {
      const [lang, region] = Utils.getLangAndRegion(text);

      if (supportLangs.includes(lang)) {
        return `${lang.toLowerCase()}-${region.toUpperCase()}`;
      } else {
        return 'en-US';
      }
    } catch (error) {
      return text;
    }
  },

  getTransTypeDisplayLabel(sportType: string, transType: string, Is1H: boolean = false) {
    const isSoccer = sportType === SportType.S;
    const transTypeDisplay =
      !isSoccer && (TransType[transType] === '1x2' || transType === 'X12')
        ? 'MoneyLine'
        : TransType[transType] ?? transType;
    return `${t(transTypeDisplay)}${Is1H ? ' / H1' : ''}`;
  },

  reactNodeToString(element: React.ReactNode): string {
    if (!element) {
      return '';
    }

    if (typeof element === 'boolean') {
      return '';
    }

    if (typeof element === 'string') {
      return element;
    }

    if (typeof element === 'number') {
      return String(element);
    }

    if (Array.isArray(element)) {
      return element.map((subElement) => Utils.reactNodeToString(subElement)).join('');
    }

    if ((element as ReactElement).props && (element as ReactElement).props.children) {
      return Utils.reactNodeToString((element as ReactElement).props.children);
    }

    if ((element as ReactElement).props && !(element as ReactElement).props.children) {
      return '';
    }

    return '';
  },

  capitalize(str: string) {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  },

  async getLangName(type: TeamLeagueType, mappingId: number, typedData: TeamLeagueSearch[]) {
    const formattedLocale = Utils.convertToLocale(localStorage.getItem('lang') ?? 'en-US');

    const foundItem: TeamLeagueSearch | undefined = typedData.find(
      (item: TeamLeagueSearch) => item.t === type && item.i === mappingId
    );

    let languagevalues = null;
    let data: string = '';

    if (foundItem !== undefined) {
      data = foundItem.v;
    }

    if (data === '') {
      if (localStorage.getItem(`${mappingId}_${type}`)) {
        languagevalues = JSON.parse(localStorage.getItem(`${mappingId}_${type}`) ?? '');
      }

      if (languagevalues === null) {
        languagevalues = await BetsAPI.getLanguageSearch(type, mappingId);
        localStorage.setItem(`${mappingId}_${type}`, JSON.stringify(languagevalues));
      }

      if (languagevalues !== null) {
        for (const [key, value] of Object.entries(languagevalues)) {
          if (key === formattedLocale) {
            data = `${value}`;
          }
        }
      }
    }

    if (data === '') {
      data = `${mappingId}`;
    }

    return data;
  },

  sortedAvailableMatchBySprotType(availableMatches: Record<SportType, any>) {
    let sortedMap = new Map<SportType, any>();
    Object.keys(SportType).forEach((key) => {
      const sportKey = key as SportType;
      const match = availableMatches[sportKey];
      if (match !== undefined) {
        sortedMap.set(sportKey, match);
      }
    });
    return sortedMap;
  },

  isSpecialBetType(betType: string) {
    return betType === BetType.HDP || betType === BetType.OU || betType === BetType.OE;
  },

  getForecastButton(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isForecast
          ? isSpecificMerchant
            ? `${cdn}/icons/common/forecast-xx5.svg`
            : `${cdn}/icons/common/forecast-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isForecast ? `${cdn}/icons/common/forecast.svg` : '';
    }
  },

  getLiveStaticButton(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isLiveStatic
          ? isSpecificMerchant
            ? `${cdn}/icons/common/live-statistics-xx5.svg`
            : `${cdn}/icons/common/live-statistics-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isLiveStatic ? `${cdn}/icons/common/live-statistics.svg` : '';
    }
  },

  getLiveStreamingButton(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isLiveStreaming
          ? isSpecificMerchant
            ? `${cdn}/icons/common/live-streaming-xx5.svg`
            : `${cdn}/icons/common/live-streaming-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isLiveStreaming ? `${cdn}/icons/common/live-streaming.svg` : '';
    }
  },

  getLiveCenterButton(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isLiveCenter
          ? isSpecificMerchant
            ? `${cdn}/icons/common/live-center-xx5.svg`
            : `${cdn}/icons/common/live-center-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isLiveCenter ? `${cdn}/icons/common/live-center.svg` : '';
    }
  },

  getPoolingRefreshCountdownIcon(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isPoolingCountdown
          ? isSpecificMerchant
            ? `${cdn}/icons/common/refresh-gold-xx5.svg`
            : `${cdn}/icons/common/refresh-gold-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isPoolingCountdown ? `${cdn}/icons/common/refresh-gold.svg` : '';
    }
  },

  getPoolingRefreshCounterIcon(
    theme: string | undefined,
    cdn: string,
    isMobile: boolean,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isPoolingCounter
          ? isMobile
            ? `${cdn}/icons/common/refresh-gray.svg`
            : `${cdn}/icons/common/refresh-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isPoolingCounter
          ? isMobile
            ? `${cdn}/icons/common/refresh-gray.svg`
            : `${cdn}/icons/common/refresh-blue.svg`
          : '';
    }
  },

  getWelcomeHeaderIcons(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isShowSumForecast
          ? isSpecificMerchant
            ? `${cdn}/icons/header/arrow-up-xx5.svg`
            : `${cdn}/icons/header/arrow-up-dark.svg`
          : isSpecificMerchant
            ? `${cdn}/icons/header/arrow-down-xx5.svg`
            : `${cdn}/icons/header/arrow-down-dark.svg`;
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isShowSumForecast
          ? `${cdn}/icons/header/arrow-up.svg`
          : `${cdn}/icons/header/arrow-down.svg`;
    }
  },

  getCarouselIcons(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams?.isCarouselArrowNext
          ? isSpecificMerchant
            ? `${cdn}/icons/common/next-xx5.svg`
            : `${cdn}/icons/common/next-dark.svg`
          : isSpecificMerchant
            ? `${cdn}/icons/common/prev-xx5.svg`
            : `${cdn}/icons/common/prev-dark.svg`;
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams?.isCarouselArrowNext ? `${cdn}/icons/common/next.svg` : `${cdn}/icons/common/prev.svg`;
    }
  },

  getReloadMiniBetListIcon(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isMiniBetListRefresh
          ? isSpecificMerchant
            ? `${cdn}/icons/header/reload-xx5.svg`
            : `${cdn}/icons/header/reload-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isMiniBetListRefresh ? `${cdn}/icons/header/reload.svg` : '';
    }
  },

  getShowMiniBetListIcon(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isShowMiniBetList
          ? isSpecificMerchant
            ? `${cdn}/icons/header/arrow-up-xx5.svg`
            : `${cdn}/icons/header/arrow-up-bet-dark.svg`
          : isSpecificMerchant
            ? `${cdn}/icons/header/arrow-down-xx5.svg`
            : `${cdn}/icons/header/arrow-down-bet-dark.svg`;
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isShowMiniBetList
          ? `${cdn}/icons/header/arrow-up.svg`
          : `${cdn}/icons/header/arrow-down.svg`;
    }
  },

  getMainAccordionIcon(
    isSpecificMerchant: boolean,
    theme: string | undefined,
    cdn: string,
    requestParams: RequestIconsParams
  ) {
    switch (theme) {
      case `theme-${ThemeType.DARK}`:
        return requestParams.isMainAccordionIcon
          ? isSpecificMerchant
            ? `${cdn}/images/utils/arrow-down-double-xx5.svg`
            : `${cdn}/images/utils/arrow-down-double-dark.svg`
          : '';
      case `theme-${ThemeType.RED}`:
      case `theme-${ThemeType.LIGHT}`:
      default:
        return requestParams.isMainAccordionIcon ? `${cdn}/images/utils/arrow-down-double.svg` : '';
    }
  },

  getNavigationBarIcons(cdn: string) {
    const isDarkTheme = Utils.isDarkTheme();
    const urlNavigationBarIcons = {
      urlBellIcon: isDarkTheme ? `${cdn}/icons/header/bell-dark.svg` : `${cdn}/icons/header/bell.svg`,
      urlBagIcon: isDarkTheme ? `${cdn}/icons/header/bag-dark.svg` : `${cdn}/icons/header/bag.svg`,
      urlSpeakerIcon: isDarkTheme ? `${cdn}/icons/header/speaker-dark.svg` : `${cdn}/icons/header/speaker.svg`,
      urlSoundOffIcon: isDarkTheme ? `${cdn}/icons/header/sound-off-dark.svg` : `${cdn}/icons/header/sound-off.svg`,
    };
    return urlNavigationBarIcons;
  },

  getTheme() {
    return useAppSelector((state) => state.ui.theme);
  },

  isDarkTheme() {
    return Utils.getTheme() === `theme-${ThemeType.DARK}` ? true : false;
  },

  isSpecificMerchant() {
    const currentMerchant = localStorage.getItem('merchantCode');
    if (!currentMerchant || !Array.isArray(specificMerchant)) {
      return false;
    }
    return specificMerchant.includes(currentMerchant);
  },

  getIconsByTheme(cdn: string, requestIconParams: RequestIconsParams) {
    let result: RequestIconsResult = {
      urlForecast: '',
      urlLiveCenter: '',
      urlLiveStatic: '',
      urlLiveStreaming: '',
      urlPoolingCountdownRefresh: '',
      urlPoolingCounterRefresh: '',
      urlWelcomeBetCredit: '',
      urlWelcomeSumForecast: '',
      urlCarousel: '',
      urlMiniBetList: '',
      urlShowMiniBetList: '',
      urlMainAccordionIcon: '',
      urlNavigationBarIcons: {
        urlBagIcon: '',
        urlBellIcon: '',
        urlSoundOffIcon: '',
        urlSpeakerIcon: '',
      },
    };

    let requestParams: RequestIconsParams = {
      isLiveCenter: requestIconParams?.isLiveCenter || false,
      isForecast: requestIconParams?.isForecast || false,
      isLiveStatic: requestIconParams?.isLiveStatic || false,
      isLiveStreaming: requestIconParams?.isLiveStreaming || false,
      isReloadHeader: requestIconParams?.isReloadHeader || false,
      isShowBetCredit: requestIconParams?.isShowBetCredit || false,
      isPoolingCountdown: requestIconParams?.isPoolingCountdown || false,
      isPoolingCounter: requestIconParams?.isPoolingCounter || false,
      isShowSumForecast: requestIconParams?.isShowSumForecast || false,
      isCarouselArrowNext: requestIconParams?.isCarouselArrowNext || false,
      isMiniBetListRefresh: requestIconParams?.isMiniBetListRefresh || false,
      isShowMiniBetList: requestIconParams?.isShowMiniBetList || false,
      isMainAccordionIcon: requestIconParams?.isMainAccordionIcon || false,
    };

    const theme = Utils.getTheme();
    const isSpecificMerchant = Utils.isSpecificMerchant();
    switch (requestIconParams.iconsComeFrom) {
      case 'OddPoolingCountdown':
        result.urlPoolingCountdownRefresh = Utils.getPoolingRefreshCountdownIcon(
          isSpecificMerchant,
          theme,
          cdn,
          requestParams
        );
        break;
      case 'OddPoolingCounter':
        result.urlPoolingCounterRefresh = Utils.getPoolingRefreshCounterIcon(theme, cdn, useIsMobile(), requestParams);
        break;
      case 'ReloadButton':
        result.urlMiniBetList = Utils.getReloadMiniBetListIcon(isSpecificMerchant, theme, cdn, requestParams);
        break;
      case 'BettingHeaderAction':
        result.urlLiveStatic = Utils.getLiveStaticButton(isSpecificMerchant, theme, cdn, requestParams);
        result.urlLiveStreaming = Utils.getLiveStreamingButton(isSpecificMerchant, theme, cdn, requestParams);
        result.urlLiveCenter = Utils.getLiveCenterButton(isSpecificMerchant, theme, cdn, requestParams);
        break;
      case 'BettingBox':
        result.urlMainAccordionIcon = Utils.getMainAccordionIcon(isSpecificMerchant, theme, cdn, requestParams);
        break;
      case 'Carousel':
        result.urlCarousel = Utils.getCarouselIcons(isSpecificMerchant, theme, cdn, requestParams);
        break;
      case 'ForecastButton':
        result.urlForecast = Utils.getForecastButton(isSpecificMerchant, theme, cdn, requestParams);
        break;
      case 'MiniBetList':
        result.urlShowMiniBetList = Utils.getShowMiniBetListIcon(isSpecificMerchant, theme, cdn, requestParams);
        break;
      case 'NavigationBar':
        result.urlNavigationBarIcons = Utils.getNavigationBarIcons(cdn);
        break;
      case 'WelcomeHeader':
        result.urlWelcomeSumForecast = Utils.getWelcomeHeaderIcons(isSpecificMerchant, theme, cdn, requestParams);
        result.urlWelcomeBetCredit = Utils.getWelcomeHeaderIcons(isSpecificMerchant, theme, cdn, requestParams);
        break;
      default:
        break;
    }

    return result;
  },

  isNumeric(n: string | number) {
    return /^-?\d+(\.\d+)?$/.test(String(n));
  },
};

export const useIsDarkTheme = () => {
  const theme = useAppSelector((state) => state.ui.theme);

  return theme === `theme-${ThemeType.DARK}`;
};

export const useIconsByTheme = (cdn: string, requestIconParams: RequestIconsParams) => {
  let result: RequestIconsResult = {
    urlForecast: '',
    urlLiveCenter: '',
    urlLiveStatic: '',
    urlLiveStreaming: '',
    urlPoolingCountdownRefresh: '',
    urlPoolingCounterRefresh: '',
    urlWelcomeBetCredit: '',
    urlWelcomeSumForecast: '',
    urlCarousel: '',
    urlMiniBetList: '',
    urlShowMiniBetList: '',
    urlMainAccordionIcon: '',
    urlNavigationBarIcons: {
      urlBagIcon: '',
      urlBellIcon: '',
      urlSoundOffIcon: '',
      urlSpeakerIcon: '',
    },
  };

  let requestParams: RequestIconsParams = {
    isLiveCenter: requestIconParams?.isLiveCenter || false,
    isForecast: requestIconParams?.isForecast || false,
    isLiveStatic: requestIconParams?.isLiveStatic || false,
    isLiveStreaming: requestIconParams?.isLiveStreaming || false,
    isReloadHeader: requestIconParams?.isReloadHeader || false,
    isShowBetCredit: requestIconParams?.isShowBetCredit || false,
    isPoolingCountdown: requestIconParams?.isPoolingCountdown || false,
    isPoolingCounter: requestIconParams?.isPoolingCounter || false,
    isShowSumForecast: requestIconParams?.isShowSumForecast || false,
    isCarouselArrowNext: requestIconParams?.isCarouselArrowNext || false,
    isMiniBetListRefresh: requestIconParams?.isMiniBetListRefresh || false,
    isShowMiniBetList: requestIconParams?.isShowMiniBetList || false,
    isMainAccordionIcon: requestIconParams?.isMainAccordionIcon || false,
  };

  const theme = useAppSelector((state) => state.ui.theme);
  const isSpecificMerchant = Utils.isSpecificMerchant();
  switch (requestIconParams.iconsComeFrom) {
    case 'OddPoolingCountdown':
      result.urlPoolingCountdownRefresh = Utils.getPoolingRefreshCountdownIcon(
        isSpecificMerchant,
        theme,
        cdn,
        requestParams
      );
      break;
    case 'OddPoolingCounter':
      result.urlPoolingCounterRefresh = Utils.getPoolingRefreshCounterIcon(theme, cdn, useIsMobile(), requestParams);
      break;
    case 'ReloadButton':
      result.urlMiniBetList = Utils.getReloadMiniBetListIcon(isSpecificMerchant, theme, cdn, requestParams);
      break;
    case 'BettingHeaderAction':
      result.urlLiveStatic = Utils.getLiveStaticButton(isSpecificMerchant, theme, cdn, requestParams);
      result.urlLiveStreaming = Utils.getLiveStreamingButton(isSpecificMerchant, theme, cdn, requestParams);
      result.urlLiveCenter = Utils.getLiveCenterButton(isSpecificMerchant, theme, cdn, requestParams);
      break;
    case 'BettingBox':
      result.urlMainAccordionIcon = Utils.getMainAccordionIcon(isSpecificMerchant, theme, cdn, requestParams);
      break;
    case 'Carousel':
      result.urlCarousel = Utils.getCarouselIcons(isSpecificMerchant, theme, cdn, requestParams);
      break;
    case 'ForecastButton':
      result.urlForecast = Utils.getForecastButton(isSpecificMerchant, theme, cdn, requestParams);
      break;
    case 'MiniBetList':
      result.urlShowMiniBetList = Utils.getShowMiniBetListIcon(isSpecificMerchant, theme, cdn, requestParams);
      break;
    case 'NavigationBar':
      result.urlNavigationBarIcons = Utils.getNavigationBarIcons(cdn);
      break;
    case 'WelcomeHeader':
      result.urlWelcomeSumForecast = Utils.getWelcomeHeaderIcons(isSpecificMerchant, theme, cdn, requestParams);
      result.urlWelcomeBetCredit = Utils.getWelcomeHeaderIcons(isSpecificMerchant, theme, cdn, requestParams);
      break;
    default:
      break;
  }

  return result;
};

export const isMerchantLogoDisplayed = () => {
  const currentMerchant = localStorage.getItem('merchantCode');
  if (!currentMerchant || !Array.isArray(merchantNeedToDisplayLogo)) {
    return false;
  }
  return merchantNeedToDisplayLogo.includes(currentMerchant);
};

export const getMerchantLogoUrl = (baseUrl: string, isLogoDisplayed: boolean) => {
  const merchantCode = localStorage.getItem('merchantCode');
  if (!isLogoDisplayed) return '';
  return `${baseUrl}/merchants/${merchantCode}/img/logo/logo.png`;
};
